import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { User, Auth } from '@core/models';
import { environment } from '@env/environment';
import { LoadingService } from '@shared/components/loading/loading.service';
import { AuthStatus } from '@shared/enums';
import { LocalStorageService } from '@shared/services';
import { catchError, map, Observable, of, throwError, timeout, shareReplay } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly baseUrl: string = environment.baseUrl;
  private http = inject(HttpClient);
  private loadingService = inject(LoadingService);
  private localStorageService = inject(LocalStorageService);

  private _currentUser = signal<User | null>(null);
  private _authStatus = signal<AuthStatus>(AuthStatus.checking);

  // Variable para evitar múltiples llamadas simultáneas
  private checkAuthInProgress = false;
  private lastCheckAuthResult: Observable<boolean> | null = null;

  public currentUser = computed(() => this._currentUser());
  public authStatus = computed(() => this._authStatus());

  login(username: string, password: string): Observable<boolean> {
    const url = `${this.baseUrl}/auth/login`;
    const body = { username, password };

    return this.http.post<Auth>(url, body).pipe(
      map(({ token, user }) => this.setAuthentication(user, token)),
      catchError((err) => throwError(() => err.error.message)),
    );
  }

  checkAuthStatus(): Observable<boolean> {
    // Si ya hay una verificación en progreso, retornar el resultado anterior
    if (this.checkAuthInProgress && this.lastCheckAuthResult) {
      return this.lastCheckAuthResult;
    }

    const url = `${this.baseUrl}/auth/check-token`;
    const token = this.localStorageService.getToken();

    if (!token) {
      this.logout();
      return of(false);
    }

    this.checkAuthInProgress = true;

    this.lastCheckAuthResult = this.http.get<Auth>(url).pipe(
      timeout(10000), // Timeout de 10 segundos para evitar llamadas infinitas
      map(({ user, token }) => this.setAuthentication(user, token)),
      catchError((error) => {
        console.error('Error checking auth status:', error);

        // Si es un error 401 (token inválido), limpiamos el estado sin hacer más llamadas
        if (error.status === 401) {
          this.logout();
          return of(false);
        }

        // Para otros errores (timeout, red de red, etc.), también limpiamos el estado
        this.logout();
        return of(false);
      }),
      shareReplay(1), // Compartir el resultado con múltiples suscriptores
    );

    // Resetear el flag cuando la operación termine
    this.lastCheckAuthResult.subscribe({
      complete: () => {
        this.checkAuthInProgress = false;
      },
      error: () => {
        this.checkAuthInProgress = false;
      }
    });

    return this.lastCheckAuthResult;
  }

  logout() {
    this.localStorageService.removeToken();
    this.loadingService.hide();
    this._currentUser.set(null);
    this._authStatus.set(AuthStatus.notAuthenticated);
    // Limpiar el estado de verificación
    this.checkAuthInProgress = false;
    this.lastCheckAuthResult = null;
  }

  private setAuthentication(user: User, token: string): boolean {
    this._currentUser.set(user);
    this._authStatus.set(AuthStatus.authenticated);
    this.localStorageService.storeToken(token);

    return true;
  }
}
